import React, { useEffect } from 'react'
import style from './style'
import Icon from '../Icon/Icon'
import Button, { ButtonType } from '../Button/Button'
import IconName from '../../helpers/types/IconName.interface'
import { makeStyles } from '@material-ui/core'
import { PropsWithChildren } from 'react'

const useStyles = makeStyles(style)

type AlertLevelPropType = 'success' | 'error'
interface PropsInterface {
    level?: AlertLevelPropType;
    onClose?(): void;
    buttonProps?: ButtonType;
    icon?: boolean | IconName;
    noBorderRadius?: boolean;
    autocloseAfter?: false | number;
    className?: string;
    'data-testid'?: string;
    marginY?: number; // px
    autoclose?: boolean; // autcloses after 5000ms if autocloseAfter not provided
}

const icons = {
    error: {
        name: 'alert',
        color: 'white'
    },
    success: {
        name: 'copy',
        color: 'primary'
    }
}

const renderIcon = (icon, level) => {
    if (icon === false) return null
    let iconName
    if (icon === true) {
        iconName = icons[level] ? icons[level].name : 'alert'
    } else {
        iconName = icon
    }

    return <Icon name={iconName} color={icons[level] ? icons[level].color : 'white'} className='alert--icon'/>
}

const renderButton = (buttonProps, level) => {
    if (!buttonProps) return null

    const { variant, color: buttonColor, className, size, ...restProps } = buttonProps
    const classes = ['alert--button', className].filter(Boolean).join(' ')
    const resolvedColor = buttonColor ?? (icons[level] ? icons[level].color : 'white')
    const resolvedVariant = variant ?? 'outlined'
    const resolvedSize = size ?? 'small'

    return <Button
        {...restProps}
        variant={resolvedVariant}
        color={resolvedColor}
        className={classes}
        size={resolvedSize}
    />
}

const renderCloseButton = (cb, level) => {
    if (!cb || (cb && typeof cb !== 'function')) {
        return null
    }

    return <Icon name='close' color={icons[level] ? icons[level].color : 'white'} className='alert--close-icon' onClick={cb} data-testid='close-alert-btn'/>
}

/**
 *
 */
const Alert = ({
    level = 'error',
    onClose,
    buttonProps,
    icon = true,
    children,
    noBorderRadius = false,
    autocloseAfter = false,
    marginY,
    autoclose = false,
    className,
    'data-testid': dataTestId = 'alert'
}: PropsWithChildren<PropsInterface>): JSX.Element => {
    if (!children) return <></>

    const styles = useStyles()
    useEffect(() => {
        if (!onClose) return

        const delay = typeof autocloseAfter === 'number' && autocloseAfter > 0
            ? autocloseAfter
            : autoclose
                ? 5000
                : null

        if (!delay) return

        const timeoutId = setTimeout(() => {
            onClose()
        }, delay)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [autoclose, autocloseAfter, onClose])
    const classes = [level, noBorderRadius && 'no-border-radius'].filter((x) => x).map((x) => `alert--${x}`).join(' ')
    let inlineStyle
    if (marginY) {
        const pxs = `${marginY}px`
        inlineStyle = { marginTop: pxs, marginBottom: pxs }
    }
    return (
        <div className={`${styles.alert} ${classes} ${className || ''}`} style={inlineStyle} data-testid={dataTestId}>
            <div className='alert--content'>
                <div className='alert--content-inner'>
                    {renderIcon(icon, level)}
                    <span className='alert--message'>
                        {children}
                    </span>
                </div>
                {renderButton(buttonProps, level)}
            </div>
            {renderCloseButton(onClose, level)}
        </div>
    )
}

/**
 *
 */
export type AlertType = PropsWithChildren<PropsInterface>
/**
 *
 */
export type AlertLevelType = AlertLevelPropType

export default Alert
